import React, { useMemo } from 'react';
import { Autocomplete, InputAdornment, TextField } from '@mui/material';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useTranslation } from 'react-i18next';
import { CollectionSiteSchema } from '../../types/collection-site.type';

export function siteLabel(site: CollectionSiteSchema): string {
  const name = site.data?.name?.trim();
  return name || site.id;
}

export function yyyyMmToDate(yyyyMm: string): Date | null {
  const [y, m] = yyyyMm.split('-').map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) return null;
  return new Date(y, m - 1, 1);
}

export function dateToYyyyMm(d: Date | null): string | null {
  if (!d || Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${month}`;
}

export interface BranchSiteSelectProps {
  sites: CollectionSiteSchema[];
  selectedSiteId: string | null;
  onSiteChange: (siteId: string | null) => void;
  disabled?: boolean;
  /** Show tree icon inside the input. Default true. */
  showStartIcon?: boolean;
  /** Show selected branch name below the field (e.g. HSE toolbar). Default false. */
  showHelperText?: boolean;
  /** Override helper text below the field. */
  helperText?: string;
}

/** Branch autocomplete only (use inside `LocalizationProvider`). Wider layout for long branch names. */
export const BranchSiteSelect: React.FC<BranchSiteSelectProps> = ({
  sites,
  selectedSiteId,
  onSiteChange,
  disabled,
  showStartIcon = true,
  showHelperText = false,
  helperText,
}) => {
  const { t } = useTranslation();
  const selectedSite = sites.find((s) => s.id === selectedSiteId) ?? null;

  const resolvedHelperText = showHelperText || helperText
    ? helperText ??
      (sites.length === 0
        ? t('common.noBranchesConfigured')
        : selectedSite
          ? siteLabel(selectedSite)
          : t('common.selectBranch'))
    : undefined;

  return (
    <Autocomplete
      sx={{
        width: '100%',
        minWidth: 0,
        '& .MuiAutocomplete-inputRoot': {
          minHeight: 40,
        },
      }}
      options={sites}
      value={selectedSite}
      onChange={(_, site) => onSiteChange(site?.id ?? null)}
      getOptionLabel={(option) => siteLabel(option)}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      disabled={disabled || sites.length === 0}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t('common.branch')}
          size="small"
          fullWidth
          placeholder={sites.length === 0 ? t('common.noBranches') : undefined}
          helperText={resolvedHelperText}
          FormHelperTextProps={
            resolvedHelperText
              ? { sx: { mx: 0, mt: 0.5, lineHeight: 1.4 } }
              : undefined
          }
          InputProps={{
            ...params.InputProps,
            startAdornment: showStartIcon ? (
              <>
                <InputAdornment position="start" sx={{ ml: 0.5, mr: 0 }}>
                  <AccountTreeOutlinedIcon sx={{ color: 'primary.main', fontSize: 22 }} />
                </InputAdornment>
                {params.InputProps.startAdornment}
              </>
            ) : (
              params.InputProps.startAdornment
            ),
          }}
          sx={{ width: '100%', minWidth: 0 }}
        />
      )}
    />
  );
};

export interface ReportingMonthPickerProps {
  selectedMonth: string;
  onMonthChange: (yyyyMm: string) => void;
  disabled?: boolean;
}

/** Month calendar picker only (use inside `LocalizationProvider`). */
export const ReportingMonthPicker: React.FC<ReportingMonthPickerProps> = ({
  selectedMonth,
  onMonthChange,
  disabled,
}) => {
  const { t } = useTranslation();
  const monthValue = useMemo(() => {
    const parsed = yyyyMmToDate(selectedMonth);
    return parsed ?? new Date();
  }, [selectedMonth]);

  return (
    <DatePicker
      views={['year', 'month']}
      openTo="month"
      format="MMMM yyyy"
      label={t('dataEntry.reportingMonth')}
      value={monthValue}
      onChange={(d) => {
        const next = dateToYyyyMm(d);
        if (next) onMonthChange(next);
      }}
      disabled={disabled}
      slotProps={{
        textField: {
          size: 'small',
          fullWidth: true,
          sx: { width: '100%', minWidth: 0 },
        },
      }}
    />
  );
};
